// Deep import (not the ~/api barrel) keeps this util loadable in unit tests:
// the barrel pulls in API services → stores/composables, which fail outside
// the app runtime.
import { HousingApplicationStatus } from "~/api/generated/models/HousingApplicationStatus";
import { housingApplicationStatusDisplayNamesHMBSite } from "./enumDisplayNames";

export interface AllowedStatus {
  value: string;
  label: string;
  disabled: boolean;
  isDefault: boolean;
}

/**
 * Active statuses in C# enum declaration order (indices 0–7).
 * This order defines the progression threshold: statuses beyond
 * the threshold index are disabled.
 */
const STATUS_PROGRESSION: HousingApplicationStatus[] = [
  HousingApplicationStatus.DRAFT,
  HousingApplicationStatus.NEW,
  HousingApplicationStatus.IN_REVIEW,
  HousingApplicationStatus.WAITLIST,
  // HousingApplicationStatus.ON_HOLD,
  HousingApplicationStatus.CANCELLED,
  // HousingApplicationStatus.LOCKED_INTERVIEWING,
  // HousingApplicationStatus.LOCKED_WITH_OFFER,
  HousingApplicationStatus.HOUSED,
];

/**
 * Statuses that must be restored as-is when the workflow resolves (e.g. coming
 * off hold): a locked applicant or one with an accepted offer still holds
 * their intake scenario entry, so the only selectable status is the one they
 * started with. Releasing them (waitlist, cancel, …) has to happen through
 * the intake scenario (reject/cancel the offer) before the hold is placed —
 * otherwise the scenario keeps an accepted/locked entry for an application
 * that no longer reflects it (#1010).
 * Legacy LockedWithOffer resumes as its modern equivalent, LockedInterviewing.
 */
const RESUME_ONLY: Partial<
  Record<HousingApplicationStatus, HousingApplicationStatus>
> = {
  [HousingApplicationStatus.OFFER_ACCEPTED]:
    HousingApplicationStatus.OFFER_ACCEPTED,
  [HousingApplicationStatus.LOCKED_INTERVIEWING]:
    HousingApplicationStatus.LOCKED_INTERVIEWING,
  [HousingApplicationStatus.LOCKED_WITH_OFFER]:
    HousingApplicationStatus.LOCKED_INTERVIEWING,
};

/**
 * Statuses that are always enabled regardless of progression threshold.
 */
const ALWAYS_ENABLED: Set<HousingApplicationStatus> = new Set([
  HousingApplicationStatus.CANCELLED,
]);

/** Normalize a status string for case/space/hyphen-insensitive matching. */
const normalize = (s: string): string => s.replace(/[\s-]/g, "").toLowerCase();

/**
 * Compute allowed statuses for the ApplicationStatusSelect dropdown.
 *
 * If statusOnCreate is a RESUME_ONLY status (locked / offer accepted), the
 * only option returned is that status itself — the applicant can only come
 * off hold back into the state they were in.
 *
 * Otherwise uses the frontend STATUS_PROGRESSION order to:
 * 1. Find the effective status's index in STATUS_PROGRESSION.
 * 2. Disable statuses whose index is greater than the threshold,
 *    unless the status is in ALWAYS_ENABLED.
 * 3. Mark the resolved status as isDefault.
 * 4. Populate labels from housingApplicationStatusDisplayNamesHMBSite.
 */
export function computeAllowedStatuses(
  statusOnCreate: string | null,
): AllowedStatus[] {
  const effectiveStatus = statusOnCreate || null;

  let thresholdIndex: number | null = null;
  let resolvedStatus: HousingApplicationStatus | null = null;

  if (effectiveStatus) {
    const normalizedEffective = normalize(effectiveStatus);

    const resumeKey = (
      Object.keys(RESUME_ONLY) as HousingApplicationStatus[]
    ).find((s) => normalize(s) === normalizedEffective);

    if (resumeKey) {
      const resumeStatus = RESUME_ONLY[resumeKey]!;
      return [
        {
          value: resumeStatus as string,
          label: housingApplicationStatusDisplayNamesHMBSite[resumeStatus]!,
          disabled: false,
          isDefault: true,
        },
      ];
    }

    const matched = STATUS_PROGRESSION.find(
      (s) => normalize(s) === normalizedEffective,
    );

    if (matched) {
      resolvedStatus = matched;
      const idx = STATUS_PROGRESSION.indexOf(resolvedStatus);
      if (idx !== -1) thresholdIndex = idx;
    }
  }

  return STATUS_PROGRESSION.map((status, i) => {
    const label = housingApplicationStatusDisplayNamesHMBSite[status]!;
    return {
      value: status as string,
      label,
      disabled:
        thresholdIndex !== null && !ALWAYS_ENABLED.has(status)
          ? i > thresholdIndex
          : false,
      isDefault: resolvedStatus !== null && status === resolvedStatus,
    };
  });
}
